// GeoForcedModal (player-frontend) — Phase R6 (2026-05-13).
//
// ════════════════════════════════════════════════════════════════════
// PURPOSE
// ════════════════════════════════════════════════════════════════════
//
// Hard-block on the player shell that fires whenever a logged-in,
// verified player has no precise GPS coordinates on file:
//
//   `user.latitude == null || user.longitude == null`
//
// Mirrors agent-frontend/src/components/GeoForcedModal.tsx — same
// non-dismissibility guarantees, same single-CTA surface, but talks
// to the PLAYER endpoints (`/api/me` PATCH + `useAuth` instead of
// `/admin/me-agent/location` + `useAdminAuth`).
//
// This is the mechanism that backfills the geo column for:
//   • legacy players signed up before the locator existed
//   • players whose admin / parent-agent just edited their structured
//     area — the parent-edit-resets-lat/lng invariant (Phase R6
//     2026-05-13) clears the player's saved coords so the locator
//     stays coherent. Without this modal the player would never be
//     re-prompted and the L1 radius search would keep pointing at
//     the old place while the L2-L4 cascade routes to the new one.
//
// ════════════════════════════════════════════════════════════════════
// HOW THE NON-DISMISSIBILITY IS ENFORCED
// ════════════════════════════════════════════════════════════════════
//
//   1. NO close button rendered.
//   2. NO X / cancel CTA. The only button is "Use my current location".
//   3. Backdrop has NO `onClick={onClose}` handler.
//   4. ESC key suppression via a `keydown` listener on `window` that
//      `preventDefault` + `stopPropagation` while mounted.
//   5. `body { overflow: hidden }` on mount — page underneath cannot
//      scroll, removing the "scroll past the modal" bypass.
//   6. z-index 9999 — above every other overlay including Toasts.
//
// The ONE legitimate exit: a successful PATCH /api/me with at least
// { latitude, longitude }. On success we call `refreshMe()` which
// re-hydrates the auth state; the gating expression in App.tsx now
// evaluates false; the modal unmounts.

import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '@baaligames/shared-ui/lib/api';
import { useAuth } from '@baaligames/shared-ui/lib/auth';
import { useToast } from '@baaligames/shared-ui/lib/toast';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import LocationCapture, {
  type GeoBag,
  EMPTY_GEO_BAG,
  isGeoLocated,
} from '@baaligames/shared-ui/components/LocationCapture';
import Icon from '@baaligames/shared-ui/components/Icon';

export default function GeoForcedModal() {
  const { refreshMe } = useAuth();
  const { push } = useToast();
  const t = useT();
  const [geo, setGeo] = useState<GeoBag>(EMPTY_GEO_BAG);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // ESC key suppression
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, []);

  // Body scroll lock
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const submit = useCallback(async () => {
    // L-6: GPS is preferred but NOT required — a pincode or a full
    // state+district+area triple satisfies the gate too, so a player who
    // refuses GPS is never locked out. Send whatever they resolved.
    if (!isGeoLocated(geo)) return;
    setBusy(true); setErr(null);
    try {
      const body: Record<string, unknown> = {};
      if (geo.latitude  != null) body.latitude  = geo.latitude;
      if (geo.longitude != null) body.longitude = geo.longitude;
      if (geo.state_id    != null) body.state_id    = geo.state_id;
      if (geo.district_id != null) body.district_id = geo.district_id;
      if (geo.area_id     != null) body.area_id     = geo.area_id;
      if (geo.pincode_id  != null) body.pincode_id  = geo.pincode_id;
      if (geo.nation)              body.nation      = geo.nation;
      await api.patch('/me', body);
      // Re-hydrate auth so the App.tsx gate re-evaluates and unmounts us.
      await refreshMe();
      push('success', t('geo.toast.success.player'));
    } catch (e) {
      setErr((e as ApiError).message || t('agent.geo.error.fallback'));
    } finally {
      setBusy(false);
    }
  }, [geo, refreshMe, push, t]);

  const canSubmit = isGeoLocated(geo) && !busy;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm
                 flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="player-geo-forced-modal-title"
      aria-describedby="player-geo-forced-modal-body"
    >
      <div
        className="w-full max-w-lg bg-white dark:bg-arena-900
                   rounded-2xl border border-arena-200/60 dark:border-arena-600/50
                   shadow-2xl my-auto"
      >
        <div className="px-5 py-4 border-b border-arena-200/40 dark:border-arena-700/60
                        bg-gradient-to-br from-fuchsia-50 to-violet-50
                        dark:from-fuchsia-500/10 dark:to-violet-500/10
                        rounded-t-2xl">
          <div className="flex items-start gap-3">
            <span className="grid place-items-center w-11 h-11 rounded-xl
                             bg-fuchsia-100 text-fuchsia-700
                             dark:bg-fuchsia-400/20 dark:text-fuchsia-300 shrink-0">
              <Icon name="my_location" size={22} filled />
            </span>
            <div className="min-w-0">
              <h2
                id="player-geo-forced-modal-title"
                className="text-lg font-extrabold text-arena-900 dark:text-white"
              >
                {t('agent.geo.title')}
              </h2>
              <p className="text-[12.5px] text-arena-700 dark:text-neon-200/80 mt-0.5 leading-relaxed">
                {t('agent.geo.subtitle')}
              </p>
            </div>
          </div>
        </div>

        <div id="player-geo-forced-modal-body" className="px-5 py-4 space-y-4">
          <div className="text-[13px] text-arena-700 dark:text-neon-100/85 leading-relaxed space-y-2">
            <p>{t('agent.geo.body.primary')}</p>
            <p className="text-[12px] text-arena-600 dark:text-neon-200/80">
              {t('agent.geo.body.secondary')}
            </p>
          </div>

          <LocationCapture
            value={geo}
            onChange={setGeo}
            mode="self"                          // player capturing OWN coords; pin → GPS → distance check
            showSavedSummary={false}             // onboarding has no pre-existing value; go straight to editor
            disabled={busy}
            inputClassName="input"               // player-frontend's brand-themed input class
          />

          {err && (
            <div className="rounded-lg bg-rose-50 dark:bg-rose-500/10
                            border border-rose-200 dark:border-rose-400/30
                            text-rose-700 dark:text-rose-200 text-xs px-3 py-2">
              {err}
            </div>
          )}

          {!canSubmit && !busy && (
            <p className="text-[11.5px] text-amber-700 dark:text-amber-300">
              {t('agent.geo.hint.disabled')}
            </p>
          )}
        </div>

        <div className="px-5 py-4 border-t border-arena-200/40 dark:border-arena-700/60
                        rounded-b-2xl">
          <button
            onClick={submit}
            disabled={!canSubmit}
            className="w-full px-4 py-3 rounded-xl text-sm font-extrabold uppercase
                       tracking-wider bg-fuchsia-600 hover:bg-fuchsia-700
                       active:bg-fuchsia-800 text-white
                       shadow-[0_2px_12px_rgba(192,38,211,0.45)]
                       inline-flex items-center justify-center gap-2
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition"
          >
            {busy ? (
              <>
                <Icon name="progress_activity" size={16} className="animate-spin" />
                {t('agent.geo.cta.saving')}
              </>
            ) : (
              <>
                <Icon name="check_circle" size={16} />
                {t('agent.geo.cta.save')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
